import { useState } from 'react';

interface LiteratureNote {
  id: string | number;
  title?: string;
  authors_year?: string;
  key_findings?: string;
  source?: string;
  paper_rq?: string;
  method_data?: string;
  limitations?: string;
  connection_to_my_research?: string;
  user_profiles?: { username?: string; avatar_url?: string };
  // other fields can be added as needed
}

interface TermDefinition {
  id: string | number;
  term?: string;
  my_definition?: string;
  usage_in_research?: string;
  user_profiles?: { username?: string; avatar_url?: string };
  // other fields can be added as needed
}

interface ResearchNotesProps {
  literatureNotes: any[];
  termDefinitions: any[];
  isAdmin?: boolean;
  currentUser?: any;
  userProfiles?: Record<string, string>;
  onUpdate?: () => void;
  onEditLiterature?: (item: any) => void;
  onDeleteLiterature?: (id: string) => void;
  onEditTerm?: (item: any) => void;
  onDeleteTerm?: (id: string) => void;
}

function UserBadge({ username }: { username?: string }) {
  if (!username) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-full text-xs font-medium border border-teal-100 dark:border-teal-700 whitespace-nowrap">
      <i className="ri-user-line text-xs"></i>
      {username}
    </span>
  );
}

/**
 * ResearchNotes component
 *
 * Displays literature notes and term definitions with search and tab functionality.
 * Handles missing data gracefully and avoids runtime crashes.
 */
export default function ResearchNotes({
  literatureNotes,
  termDefinitions,
  isAdmin = false,
  currentUser,
  userProfiles = {},
  onUpdate,
  onEditLiterature,
  onDeleteLiterature,
  onEditTerm,
  onDeleteTerm,
}: ResearchNotesProps) {
  const [activeTab, setActiveTab] = useState<'literature' | 'terms'>('literature');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'literature' | 'term'; id: string } | null>(null);

  // Check if user can edit/delete this item
  const canModify = (item: any) => {
    if (isAdmin) return true;
    if (!currentUser) return false;
    return item.user_id === currentUser.id;
  };

  // Defensive filtering – protect against undefined fields
  const filteredLiterature = literatureNotes.filter((note) => {
    const query = searchQuery.toLowerCase();
    return (
      (note.title ?? '').toLowerCase().includes(query) ||
      (note.authors_year ?? '').toLowerCase().includes(query) ||
      (note.key_findings ?? '').toLowerCase().includes(query)
    );
  });

  const filteredTerms = termDefinitions.filter((term) => {
    const query = searchQuery.toLowerCase();
    return (
      (term.term ?? '').toLowerCase().includes(query) ||
      (term.my_definition ?? '').toLowerCase().includes(query)
    );
  });

  const handleDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'literature' && onDeleteLiterature) {
      onDeleteLiterature(deleteConfirm.id);
    } else if (deleteConfirm.type === 'term' && onDeleteTerm) {
      onDeleteTerm(deleteConfirm.id);
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="space-y-5">
      {/* Header with title and search */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
            <i className="ri-book-line text-teal-600 dark:text-teal-400"></i>
          </div>
          研究ノート
        </h2>

        <div className="relative flex-1 max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm"></i>
          <input
            type="text"
            placeholder="検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          />
        </div>
      </div>

      {/* Main container */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('literature')}
            className={`flex-1 px-5 py-3.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'literature'
                ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-b-2 border-teal-500'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            <i className="ri-article-line"></i>
            先行研究メモ
            <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-semibold px-2 py-0.5 rounded-full">
              {filteredLiterature.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('terms')}
            className={`flex-1 px-5 py-3.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'terms'
                ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-b-2 border-teal-500'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            <i className="ri-book-open-line"></i>
            用語・定義
            <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-semibold px-2 py-0.5 rounded-full">
              {filteredTerms.length}
            </span>
          </button>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6">
          {activeTab === 'literature' && (
            <div className="space-y-4">
              {filteredLiterature.length === 0 ? (
                <div className="text-center py-10">
                  <i className="ri-file-search-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {searchQuery ? '検索結果がありません' : '先行研究メモがまだ登録されていません'}
                  </p>
                </div>
              ) : (
                filteredLiterature.map((note) => {
                  const userCanModify = canModify(note);
                  const username = note.user_profiles?.username || null;
                  return (
                    <div key={note.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600 p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-800 dark:text-white">{note.title ?? '（タイトルなし）'}</h3>
                          {username && <div className="mt-1"><UserBadge username={username} /></div>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {note.source && (
                            <a href={note.source} target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:text-teal-700 whitespace-nowrap cursor-pointer">
                              <i className="ri-external-link-line"></i>
                            </a>
                          )}
                          {userCanModify && onEditLiterature && (
                            <button onClick={() => onEditLiterature(note)} className="w-7 h-7 flex items-center justify-center text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/40 rounded-lg transition-colors cursor-pointer" title="編集">
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                          )}
                          {userCanModify && onDeleteLiterature && (
                            <button onClick={() => setDeleteConfirm({ type: 'literature', id: String(note.id) })} className="w-7 h-7 flex items-center justify-center text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors cursor-pointer" title="削除">
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          )}
                        </div>
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{note.authors_year ?? ''}</p>

                      <div className="space-y-2.5 text-sm">
                        <div>
                          <span className="font-semibold text-gray-600 dark:text-gray-300 text-xs">RQ: </span>
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{note.paper_rq ?? ''}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-600 dark:text-gray-300 text-xs">方法・データ: </span>
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{note.method_data ?? ''}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-600 dark:text-gray-300 text-xs">主要な発見: </span>
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{note.key_findings ?? ''}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-600 dark:text-gray-300 text-xs">限界: </span>
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{note.limitations ?? ''}</span>
                        </div>
                        <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3 mt-2">
                          <span className="font-semibold text-teal-700 dark:text-teal-400 text-xs">自分の研究との関連: </span>
                          <span className="text-gray-700 dark:text-gray-300 text-xs">{note.connection_to_my_research ?? ''}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'terms' && (
            <div className="space-y-3">
              {filteredTerms.length === 0 ? (
                <div className="text-center py-10">
                  <i className="ri-book-open-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {searchQuery ? '検索結果がありません' : '用語・定義がまだ登録されていません'}
                  </p>
                </div>
              ) : (
                filteredTerms.map((term) => {
                  const userCanModify = canModify(term);
                  const username = term.user_profiles?.username || null;
                  return (
                    <div key={term.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600 p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-800 dark:text-white">{term.term ?? ''}</h3>
                          {username && <div className="mt-1"><UserBadge username={username} /></div>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {userCanModify && onEditTerm && (
                            <button onClick={() => onEditTerm(term)} className="w-7 h-7 flex items-center justify-center text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/40 rounded-lg transition-colors cursor-pointer" title="編集">
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                          )}
                          {userCanModify && onDeleteTerm && (
                            <button onClick={() => setDeleteConfirm({ type: 'term', id: String(term.id) })} className="w-7 h-7 flex items-center justify-center text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors cursor-pointer" title="削除">
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed mb-3">{term.my_definition ?? ''}</p>
                      <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3">
                        <span className="text-xs font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wider">研究での使用</span>
                        <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">{term.usage_in_research ?? ''}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-rose-100 dark:bg-rose-900/40 rounded-full">
                <i className="ri-alert-line text-rose-600 dark:text-rose-400 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">削除の確認</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">この操作は取り消せません</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
              この{deleteConfirm.type === 'literature' ? '先行研究メモ' : '用語・定義'}を削除してもよろしいですか？
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">キャンセル</button>
              <button onClick={handleDelete} className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">削除する</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}